import { ActionConfirmation, usePublishActionOutcome, useHasActionOutcomeHost, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api.js";
import {
  dayKey,
  formatDate,
  formatDay,
  formatTime,
  londonWallClockAttempt,
} from "../format.js";
import { idempotencyKey } from "./api.js";
import {
  availabilityDateOffset,
  availabilityWindow,
  fourWeeksFrom,
  groupAvailabilitySlots,
  moveAvailabilityWindow,
} from "./availabilityRange.js";
import { bookingAttemptAfterFailure, bookingFailureKind, uncertainBookingMessageFor } from "./attempt.js";
import { NotifyToggle } from "./notify.js";
import {
  getRescheduleContext,
  type RescheduleContext,
} from "./rescheduleContext.js";
import {
  type BulkReschedulePreview,
  type BulkRescheduleResult,
  staffBooking,
  type StaffAvailability,
} from "./staff.js";
import { measureAction } from "../telemetry/actionTiming.js";

/**
 * Moving one lesson, from the list it is already in.
 *
 * The ordinary diary path is scheduler-shaped: resolve the appointment's exact
 * provider/service identity from the scoped Hub read model, ask WMF-SIte for
 * authoritative availability, select a returned slot, then run the protected
 * reschedule preview and mutation. Provider/service display names are never
 * used as identity.
 *
 * Manual date/time entry remains deliberately separate as an advanced fallback.
 * It is also how a dragged target outside the published grid is represented:
 * absence from the grid is not treated as proof of a conflict. Preview asks
 * WMF-SIte whether the time is truly unavailable or merely outside normal
 * working hours, and only the latter can be explicitly overridden.
 *
 * Preview is still not permission: FastAPI rechecks immediately before the
 * write, so a slot becoming stale after it was shown is an ordinary refusal and
 * is rendered as one.
 */

/** The message a failed mutation deserves: the server's own words, or a safe one. */
export function tutorMoveFailure(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  return "Something went wrong. Nothing was changed.";
}

/** A failed alternatives read must never imply that a guessed provider/service is safe. */
export function tutorMoveAvailabilityFailure(cause: unknown): string {
  if (
    cause instanceof ApiError &&
    (cause.status === 403 || cause.status === 404)
  ) {
    return "Available times could not be loaded for this appointment.";
  }
  if (
    cause instanceof ApiError &&
    (cause.code === "upstream_unavailable" || cause.status >= 500)
  ) {
    return "Available times are temporarily unavailable. You can try again or enter a time manually.";
  }
  if (cause instanceof ApiError) return cause.message;
  return "Available times could not be loaded. You can try again or enter a time manually.";
}

/**
 * Refuse an availability answer for a different pair rather than drawing it.
 *
 * WMF-SIte echoes both top-level ids and each slot's provider. Checking them is
 * cheap and means an upstream/proxy regression cannot put one tutor's times
 * under another appointment's Reschedule button.
 */
export function tutorMoveAvailabilityMatches(
  context: RescheduleContext,
  availability: StaffAvailability,
): boolean {
  return (
    availability.provider_id === context.provider_id &&
    availability.service_id === context.service_id &&
    availability.slots.every((slot) => slot.provider_id === context.provider_id)
  );
}

/**
 * The key belongs to the attempt, not to the request.
 *
 * A timeout or a 5xx leaves the outcome unknown, and a fresh key on the retry
 * would move the lesson twice. Only a definite rejection — the server saying it
 * did nothing — earns a new one.
 */
export function tutorMoveRetryKey(
  currentKey: string,
  cause: unknown,
  createKey: () => string,
): string {
  return bookingAttemptAfterFailure(currentKey, cause, createKey).key;
}

export type MoveTarget =
  | { ok: true; startsAt: string }
  | { ok: false; message: string };

/**
 * The typed date and time, as the instant FastAPI will be asked for.
 *
 * The two clock-change failures are named rather than lumped in with a typing
 * mistake, because they are not one: the date is fine, the time is fine, and
 * that combination of them happens twice this year or not at all. "Check the
 * date" would send somebody to look at something that was never wrong.
 */
export function tutorMoveTarget(
  date: string,
  time: string,
  currentStartsAt: string,
): MoveTarget {
  const attempt = londonWallClockAttempt(date, time);
  if (!attempt.ok) {
    if (attempt.reason === "gap") {
      return {
        ok: false,
        message:
          "The clocks go forward that morning, so that time does not happen. Pick another.",
      };
    }
    if (attempt.reason === "ambiguous") {
      return {
        ok: false,
        message:
          "The clocks go back that morning, so that time happens twice. Pick another.",
      };
    }
    return {
      ok: false,
      message: "Give a date and a time for the lesson to move to.",
    };
  }

  if (Date.parse(attempt.iso) === Date.parse(currentStartsAt)) {
    return { ok: false, message: "That is when the lesson already is." };
  }

  return { ok: true, startsAt: attempt.iso };
}

/** A returned availability slot, guarded against the lesson's current instant. */
export function tutorMoveSlotTarget(
  startsAt: string | null,
  currentStartsAt: string,
): MoveTarget {
  if (startsAt === null)
    return { ok: false, message: "Choose an available time." };
  if (Date.parse(startsAt) === Date.parse(currentStartsAt)) {
    return { ok: false, message: "That is when the lesson already is." };
  }
  return { ok: true, startsAt };
}

/** Whether there is enough on the form to ask what would happen. */
export function tutorMoveCheckDisabled(
  busy: boolean,
  target: MoveTarget,
): boolean {
  return busy || !target.ok;
}

export interface MoveVerdict {
  canMove: boolean;
  canOverrideWorkingHours: boolean;
  message: string;
}

/**
 * One lesson's answer, out of a preview shaped for many.
 *
 * A missing item is treated as a refusal rather than as permission. A working-
 * hours warning is its own result: it is not permission until the tutor has
 * explicitly accepted it and a second preview is run with the override flag.
 */
export function tutorMovePreviewVerdict(
  preview: BulkReschedulePreview,
): MoveVerdict {
  const item = preview.items[0];
  if (!item) {
    return {
      canMove: false,
      canOverrideWorkingHours: false,
      message: "The booking system did not answer for this lesson.",
    };
  }
  if (item.already_at_target) {
    return {
      canMove: false,
      canOverrideWorkingHours: false,
      message: "That is already when this lesson is.",
    };
  }
  const scheduleOverrideAvailable =
    item.schedule_override_available ?? item.working_hours_override_available;
  if (!item.can_reschedule && scheduleOverrideAvailable) {
    return {
      canMove: false,
      canOverrideWorkingHours: true,
      message:
        item.message ??
        "This time needs a staff schedule override and may be overridden after confirmation.",
    };
  }
  if (!item.can_reschedule) {
    return {
      canMove: false,
      canOverrideWorkingHours: false,
      message: item.message ?? "This lesson cannot be moved to that time.",
    };
  }
  return {
    canMove: true,
    canOverrideWorkingHours: false,
    message: `It would move to ${formatDate(item.requested_start)} at ${formatTime(item.requested_start)}.`,
  };
}

/** What actually happened, including the retry that found the work already done. */
export function tutorMoveOutcome(result: BulkRescheduleResult): string {
  const row = result.results[0];
  if (!row) {
    return "The booking system did not say what happened. Check the lesson before trying again.";
  }
  if (row.outcome_unknown) {
    const cause = new ApiError(
      503,
      row.message ?? "The booking system did not confirm the result.",
      "upstream_unavailable",
      {
        bookingStage: row.booking_stage,
        requestId: row.request_reference,
        bookingDiagnostics: row.override_diagnostics,
      },
    );
    return uncertainBookingMessageFor(
      cause,
      "The booking system did not confirm whether this lesson moved. Reload before changing it; retrying uses the same attempt.",
    );
  }
  if (!row.success) {
    return `Not moved: ${row.message ?? "no reason was given"}. The lesson is still where it was.`;
  }
  const landed = row.final_start
    ? ` to ${formatDate(row.final_start)} at ${formatTime(row.final_start)}`
    : "";
  if (row.replayed) {
    return `Already moved${landed} by an earlier attempt. It was not moved again.`;
  }
  return `Moved${landed}. Lessons are refreshing.`;
}

/**
 * A time input's value for an instant, in London.
 *
 * `formatTime` renders midnight as "24:00" on some engines, which no time input
 * will accept. Nothing is taught at midnight, so this is folded rather than
 * guarded against.
 */
function timeValue(iso: string): string {
  const shown = formatTime(iso);
  return shown.startsWith("24") ? `00${shown.slice(2)}` : shown;
}

/** The one compact week shown from the authoritative 28-day response. */
export function tutorMoveWeekSlots(
  slots: StaffAvailability["slots"],
  searchDate: string,
): StaffAvailability["slots"] {
  const { endDate } = availabilityWindow(searchDate, "week");
  return slots.filter((slot) => {
    const slotDate = dayKey(slot.starts_at);
    return slotDate >= searchDate && slotDate < endDate;
  });
}

function rangeDateInstant(date: string): string {
  return `${date}T12:00:00.000Z`;
}

function tutorMoveRangeLabel(searchDate: string): string {
  const { endDate } = availabilityWindow(searchDate, "week");
  const finalDate = availabilityDateOffset(endDate, -1);
  return `${formatDate(rangeDateInstant(searchDate))} – ${formatDate(rangeDateInstant(finalDate))}`;
}

type MoveBusy = "context" | "availability" | "preview" | "move" | null;

/**
 * The Move back button outlives the drawer that created it: closing the drawer
 * hands this receipt to the page's host, and the button is rendered from there.
 * It therefore publishes through the host it is rendered under, not through
 * the move form that has since unmounted.
 */
function MoveBackAction({
  appointmentId,
  startsAt,
  notify,
  studentName,
  onMoved,
  onLocalOutcome,
}: {
  appointmentId: number;
  startsAt: string;
  notify: boolean;
  studentName: string;
  onMoved?: (startsAt: string) => void;
  onLocalOutcome: (outcome: ActionOutcome) => void;
}) {
  const publish = usePublishActionOutcome();
  const used = useRef(false);
  const [disabled, setDisabled] = useState(false);

  function finish(outcome: ActionOutcome) {
    if (!publish(outcome)) onLocalOutcome(outcome);
  }

  async function moveBack() {
    try {
      const backPreview = await staffBooking.bulkReschedulePreview(
        [{ appointment_id: appointmentId, starts_at: startsAt }],
        "",
      );
      if (backPreview.items[0]?.can_reschedule !== true) {
        finish({
          variant: "attention",
          title: "Could not move back",
          description: studentName,
          details: [{ value: "Could not move back: that time is no longer free." }],
        });
        return;
      }

      const result = await measureAction("lesson.move", () =>
        staffBooking.bulkReschedule(
          [{ appointment_id: appointmentId, starts_at: startsAt }],
          "",
          idempotencyKey(),
          notify,
        ),
      );
      const row = result.results[0];
      if (!row || row.outcome_unknown) {
        finish({
          variant: "uncertain",
          title: "Move-back outcome needs checking",
          description: studentName,
          details: [{ value: tutorMoveOutcome(result) }],
        });
        return;
      }

      finish({
        variant: row.success ? "success" : "attention",
        title: row.success ? "Lesson moved back" : "Could not move back",
        description: studentName,
        details: [{ value: tutorMoveOutcome(result) }],
      });
      if (row.success && row.final_start) onMoved?.(row.final_start);
    } catch (cause) {
      const isUncertain = bookingFailureKind(cause) === "uncertain";
      finish({
        variant: isUncertain ? "uncertain" : "attention",
        title: isUncertain ? "Move-back outcome needs checking" : "Could not move back",
        description: studentName,
        details: [{ value: tutorMoveFailure(cause) }],
      });
    }
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (used.current) return;
        used.current = true;
        setDisabled(true);
        void moveBack();
      }}
    >
      Move back
    </button>
  );
}


interface PendingTutorMoveAttempt {
  appointmentId: number;
  startsAt: string;
  key: string;
  notify: boolean;
  overrideWorkingHours: boolean;
  signature: string;
}

type TutorMoveRecovery = {
  attempt: PendingTutorMoveAttempt | null;
  problem: string | null;
};

function tutorMoveAttemptSignature(
  attempt: Omit<PendingTutorMoveAttempt, "signature">,
): string {
  return JSON.stringify({
    appointmentId: attempt.appointmentId,
    startsAt: attempt.startsAt,
    key: attempt.key,
    notify: attempt.notify,
    overrideWorkingHours: attempt.overrideWorkingHours,
  });
}

function readPendingTutorMoveAttempt(
  storageKey: string,
  appointmentId: number,
): TutorMoveRecovery {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw === null) return { attempt: null, problem: null };
    const value: unknown = JSON.parse(raw);
    if (value === null || typeof value !== "object") throw new Error("invalid saved move");
    const saved = value as Partial<PendingTutorMoveAttempt>;
    if (
      !Number.isSafeInteger(saved.appointmentId) ||
      saved.appointmentId !== appointmentId ||
      typeof saved.startsAt !== "string" ||
      !Number.isFinite(Date.parse(saved.startsAt)) ||
      typeof saved.key !== "string" ||
      saved.key.length === 0 ||
      typeof saved.notify !== "boolean" ||
      typeof saved.overrideWorkingHours !== "boolean" ||
      typeof saved.signature !== "string"
    ) throw new Error("invalid saved move");
    const attempt = {
      appointmentId: saved.appointmentId as number,
      startsAt: saved.startsAt,
      key: saved.key,
      notify: saved.notify,
      overrideWorkingHours: saved.overrideWorkingHours,
    };
    if (saved.signature !== tutorMoveAttemptSignature(attempt)) {
      throw new Error("saved move changed");
    }
    return { attempt: { ...attempt, signature: saved.signature }, problem: null };
  } catch {
    return {
      attempt: null,
      problem: "A saved move request could not be checked. Review the lesson status before trying another move.",
    };
  }
}

export function TutorMoveLesson({
  bookingId = null,
  appointmentId,
  studentName,
  startsAt,
  serviceName,
  onClose,
  onMoved,
  onBusyChange,
  initialStartsAt = null,
  recoveryUserId = "staff",
}: {
  /** Hub booking id used only to read the appointment's authoritative pair. */
  bookingId?: string | null;
  /**
   * Existing numeric reschedule target. Kept unchanged from the established
   * preview/write contract; provider/service ids never replace it as authority.
   */
  appointmentId: number;
  studentName: string;
  startsAt: string;
  serviceName: string | null;
  onClose: () => void;
  /** Only a confirmed successful move may close the flow and reload its host. */
  onMoved?: (startsAt: string) => void;
  onBusyChange?: (busy: boolean) => void;
  /** Calendar drag target. Hint only; never an optimistic move. */
  initialStartsAt?: string | null;
  /** Scopes an uncertain move recovery record to the signed-in staff member. */
  recoveryUserId?: string | null;
}) {
  const storageKey =
    "wmf:tutor-move:v1:" + (recoveryUserId ?? "staff") + ":" + appointmentId;
  const [recovery] = useState(() =>
    readPendingTutorMoveAttempt(storageKey, appointmentId),
  );
  const recoveredAttempt = recovery.attempt;
  const initialTarget = recoveredAttempt?.startsAt ?? initialStartsAt ?? startsAt;
  const initialSearchDate = dayKey(initialTarget);
  const [searchDate, setSearchDate] = useState(initialSearchDate);
  const [availabilityStart, setAvailabilityStart] = useState(initialSearchDate);
  const [manual, setManual] = useState(() => recoveredAttempt === null && bookingId === null);
  const [advancedOpen, setAdvancedOpen] = useState(() => bookingId === null);
  const [date, setDate] = useState(initialSearchDate);
  const [time, setTime] = useState(() => timeValue(initialTarget));
  const [context, setContext] = useState<RescheduleContext | null>(null);
  const [slots, setSlots] = useState<StaffAvailability["slots"] | null>(null);
  const [selectedStart, setSelectedStart] = useState<string | null>(
    recoveredAttempt?.startsAt ?? null,
  );
  const [availabilityProblem, setAvailabilityProblem] = useState<string | null>(
    null,
  );
  const pendingDragTarget = useRef(initialStartsAt);
  const [notify, setNotify] = useState(recoveredAttempt?.notify ?? true);
  const [preview, setPreview] = useState<BulkReschedulePreview | null>(null);
  const [overrideWorkingHours, setOverrideWorkingHours] = useState(
    recoveredAttempt?.overrideWorkingHours ?? false,
  );
  const [error, setError] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [busy, setBusy] = useState<MoveBusy>(null);
  const [coarsePointer, setCoarsePointer] = useState(false);
  const publish = usePublishActionOutcome();
  const hasHost = useHasActionOutcomeHost();
  function report(outcome: ActionOutcome) { setDone(outcome); publish(outcome); }
  const [done, setDone] = useState<ActionOutcome | null>(null);
  const [uncertain, setUncertain] = useState(recoveredAttempt !== null);
  const [key, setKey] = useState(recoveredAttempt?.key ?? idempotencyKey);
  const [pendingAttempt, setPendingAttempt] =
    useState<PendingTutorMoveAttempt | null>(recoveredAttempt);
  const [recoveryProblem, setRecoveryProblem] = useState<string | null>(
    recovery.problem,
  );
  const [loadAttempt, setLoadAttempt] = useState(0);
  const previewRequest = useRef(0);
  const autoPreviewTarget = useRef<string | null>(null);

  const isBusy = busy !== null;
  const targetChangingDisabled =
    busy === "context" || busy === "availability" || busy === "move" ||
    pendingAttempt !== null || recoveryProblem !== null;
  const visibleSlots =
    slots === null ? null : tutorMoveWeekSlots(slots, searchDate);
  const target = manual
    ? tutorMoveTarget(date, time, startsAt)
    : tutorMoveSlotTarget(selectedStart, startsAt);
  const targetStartsAt = target.ok ? target.startsAt : null;
  const verdict = preview ? tutorMovePreviewVerdict(preview) : null;
  const setMoveBusy = useCallback((nextBusy: MoveBusy) => {
    setBusy(nextBusy);
    onBusyChange?.(nextBusy !== null);
  }, [onBusyChange]);

  /** A changed target is a different idempotent attempt. */
  function resetAttempt() {
    if (pendingAttempt !== null || recoveryProblem !== null) return;
    previewRequest.current += 1;
    autoPreviewTarget.current = null;
    setPreview(null);
    setOverrideWorkingHours(false);
    setError(null);
    setPreviewFailed(false);
    if (busy === "preview") setMoveBusy(null);
    setKey(idempotencyKey());
  }

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(pointer: coarse)");
    const update = () => setCoarsePointer(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  // Loading the appointment's identity and its available times is a read. It
  // must not drive `onBusyChange`: a host drawer treats that as "a mutation is
  // in flight, block dismissal", and there is nothing here worth protecting a
  // half-open drawer for. `busy` stays local so the panel can still show its
  // own "Loading…" state and disable inputs.
  useEffect(() => {
    if (bookingId === null) return;

    let active = true;
    setBusy("context");
    setContext(null);
    setSlots(null);
    setAvailabilityProblem(null);

    void getRescheduleContext(bookingId)
      .then((result) => {
        if (!active) return;
        setContext(result);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setAvailabilityProblem(tutorMoveAvailabilityFailure(cause));
      })
      .finally(() => {
        if (!active) return;
        setBusy(null);
      });

    return () => {
      active = false;
    };
  }, [bookingId, loadAttempt]);

  useEffect(() => {
    if (context === null || availabilityStart === "") return;

    let active = true;
    setBusy("availability");
    setSlots(null);
    setAvailabilityProblem(null);

    void staffBooking
      .availability({
        provider_id: String(context.provider_id),
        service_id: String(context.service_id),
        start_date: availabilityStart,
        end_date: fourWeeksFrom(availabilityStart),
      })
      .then((result) => {
        if (!active) return;
        if (!tutorMoveAvailabilityMatches(context, result)) {
          setAvailabilityProblem(
            "The booking system returned times for a different tutor or service, so none are shown.",
          );
          setSlots([]);
          return;
        }

        setSlots(result.slots);
        const dragged = pendingDragTarget.current;
        if (dragged !== null) {
          pendingDragTarget.current = null;
          const available = result.slots.some(
            (slot) => slot.starts_at === dragged,
          );
          if (available) {
            setSelectedStart(dragged);
          } else {
            // Missing from public availability is not proof of a conflict. Keep
            // the exact dragged wall-clock time and let preview classify it as
            // hard-unavailable or a warning that may be overridden.
            setManual(true);
            setAdvancedOpen(true);
            setDate(dayKey(dragged));
            setTime(timeValue(dragged));
            setSelectedStart(null);
          }
        }
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setAvailabilityProblem(tutorMoveAvailabilityFailure(cause));
        setSlots(null);
      })
      .finally(() => {
        if (!active) return;
        setBusy(null);
      });

    return () => {
      active = false;
    };
  }, [context, availabilityStart]);

  // Re-run the identity read (which cascades into a fresh availability read).
  function retryLoads() {
    setAvailabilityProblem(null);
    setLoadAttempt((attempt) => attempt + 1);
  }

  function chooseSearchDate(nextDate: string) {
    if (nextDate === "" || nextDate === searchDate) return;

    const loadedEnd = fourWeeksFrom(availabilityStart);
    const nextWeekEnd = availabilityWindow(nextDate, "week").endDate;
    const canReuseLoadedSlots =
      slots !== null &&
      availabilityProblem === null &&
      nextDate >= availabilityStart &&
      nextWeekEnd <= loadedEnd;

    setSearchDate(nextDate);

    // Browsing alternatives must not silently throw away a typed manual target.
    // In slot mode, however, a different visible week invalidates that slot's
    // preview and selection just as choosing a different slot would.
    if (!manual) {
      setSelectedStart(null);
      resetAttempt();
    }

    if (!canReuseLoadedSlots) {
      setAvailabilityStart(nextDate);
      setSlots(null);
      setAvailabilityProblem(null);
    }
  }

  function shift(direction: -1 | 1) {
    if (bookingId === null) {
      retargetManual(() =>
        setDate(moveAvailabilityWindow(date, "week", direction)),
      );
      return;
    }
    chooseSearchDate(moveAvailabilityWindow(searchDate, "week", direction));
  }

  function chooseSlot(startsAtSlot: string) {
    setManual(false);
    setAdvancedOpen(false);
    setSelectedStart(startsAtSlot);
    resetAttempt();
  }

  function chooseManual() {
    setManual(true);
    setAdvancedOpen(true);
    setDate(searchDate);
    setSelectedStart(null);
    resetAttempt();
  }

  function chooseAvailableTimes() {
    setManual(false);
    setAdvancedOpen(false);
    resetAttempt();
  }

  function handleAdvancedToggle(nextOpen: boolean) {
    if (pendingAttempt !== null || recoveryProblem !== null) return;
    if (nextOpen === advancedOpen) return;
    if (nextOpen) {
      chooseManual();
    } else {
      chooseAvailableTimes();
    }
  }

  function retargetManual(next: () => void) {
    next();
    resetAttempt();
  }

  function chooseQuickTarget(days: number) {
    const nextDate = availabilityDateOffset(dayKey(startsAt), days);
    const loadedEnd = fourWeeksFrom(availabilityStart);
    const nextWeekEnd = availabilityWindow(nextDate, "week").endDate;
    const canReuseLoadedSlots =
      slots !== null &&
      availabilityProblem === null &&
      nextDate >= availabilityStart &&
      nextWeekEnd <= loadedEnd;
    setManual(true);
    setAdvancedOpen(true);
    setSelectedStart(null);
    setDate(nextDate);
    setTime(timeValue(startsAt));
    setSearchDate(nextDate);
    resetAttempt();
    if (!canReuseLoadedSlots) {
      setAvailabilityStart(nextDate);
      setSlots(null);
      setAvailabilityProblem(null);
    }
  }

  const look = useCallback(async (
    useWorkingHoursOverride = false,
    requestedStartsAt: string | null = targetStartsAt,
  ): Promise<MoveVerdict | null> => {
    if (
      requestedStartsAt === null ||
      pendingAttempt !== null ||
      recoveryProblem !== null
    ) return null;
    const request = ++previewRequest.current;
    setMoveBusy("preview");
    setError(null);
    setPreviewFailed(false);
    setOverrideWorkingHours(useWorkingHoursOverride);
    try {
      const nextPreview = await staffBooking.bulkReschedulePreview(
        [{ appointment_id: appointmentId, starts_at: requestedStartsAt }],
        "",
        useWorkingHoursOverride,
      );
      if (request !== previewRequest.current) return null;
      setPreview(nextPreview);
      return tutorMovePreviewVerdict(nextPreview);
    } catch (cause) {
      if (request === previewRequest.current) {
        setError(tutorMoveFailure(cause));
        setPreview(null);
        setOverrideWorkingHours(false);
        setPreviewFailed(true);
      }
      return null;
    } finally {
      if (request === previewRequest.current) setMoveBusy(null);
    }
  }, [appointmentId, pendingAttempt, recoveryProblem, setMoveBusy, targetStartsAt]);

  useEffect(() => {
    if (
      !target.ok ||
      targetStartsAt === null ||
      preview !== null ||
      busy !== null ||
      done !== null ||
      pendingAttempt !== null ||
      recoveryProblem !== null ||
      uncertain ||
      autoPreviewTarget.current === targetStartsAt
    ) return;

    const requestedStartsAt = targetStartsAt;
    autoPreviewTarget.current = requestedStartsAt;
    let timeout: number | null = null;
    const runPreview = () => { void look(false, requestedStartsAt); };
    if (manual) timeout = window.setTimeout(runPreview, 400);
    else runPreview();

    return () => {
      if (timeout !== null) window.clearTimeout(timeout);
    };
  }, [
    target.ok,
    targetStartsAt,
    manual,
    preview,
    busy,
    done,
    pendingAttempt,
    recoveryProblem,
    uncertain,
    look,
  ]);

  function clearSavedMoveAttempt(): boolean {
    try {
      sessionStorage.removeItem(storageKey);
      setPendingAttempt(null);
      setRecoveryProblem(null);
      return true;
    } catch {
      setError("The saved move could not be cleared from this browser. Keep this review open and check the same request again.");
      return false;
    }
  }

  /**
   * A record that cannot be validated cannot be retried, and it locks the
   * controls. Drop it and re-read the appointment so the tutor can see where the
   * lesson actually is before trying another move.
   */
  function clearAndCheckLessonStatus() {
    if (!clearSavedMoveAttempt()) return;
    setError(null);
    setUncertain(false);
    setLoadAttempt((attempt) => attempt + 1);
  }

  async function submitMoveAttempt(attempt: PendingTutorMoveAttempt) {
    setMoveBusy("move");
    setError(null);
    try {
      const result = await measureAction("lesson.move", () =>
        staffBooking.bulkReschedule(
          [{ appointment_id: attempt.appointmentId, starts_at: attempt.startsAt }],
          "",
          attempt.key,
          attempt.notify,
          attempt.overrideWorkingHours,
        ),
      );
      const row = result.results[0];
      if (
        result.requested_count !== 1 ||
        result.results.length !== 1 ||
        !row ||
        row.appointment_id !== attempt.appointmentId ||
        row.outcome_unknown
      ) {
        const message = row?.appointment_id === attempt.appointmentId
          ? tutorMoveOutcome(result)
          : "The booking service did not confirm a matching result for this saved move. Check or retry this exact request.";
        setUncertain(true);
        setError(message);
        publish({
          variant: "uncertain",
          title: "Move outcome needs checking",
          description: studentName,
          details: [{ value: message }],
        });
        return;
      }

      const savedRequestCleared = clearSavedMoveAttempt();
      setUncertain(!savedRequestCleared);
      report({
        variant: row.success ? "success" : "attention",
        title: row.success ? "Lesson moved" : "Lesson not moved",
        description: studentName,
        details: [
          { value: tutorMoveOutcome(result) },
          ...(row.success && !row.replayed && attempt.notify
            ? [{ value: "Move back will email the family again." }]
            : []),
        ],
        ...(row.success && !row.replayed
          ? {
              action: (
                <MoveBackAction
                  appointmentId={attempt.appointmentId}
                  startsAt={startsAt}
                  notify={attempt.notify}
                  studentName={studentName}
                  onMoved={onMoved}
                  onLocalOutcome={setDone}
                />
              ),
            }
          : {}),
      });
      if (row.success && row.final_start) onMoved?.(row.final_start);
    } catch (cause) {
      const kind = bookingFailureKind(cause);
      if (kind === "uncertain") {
        setUncertain(true);
        setError(tutorMoveFailure(cause));
        publish({
          variant: "uncertain",
          title: "Move outcome needs checking",
          description: studentName,
          details: [{ value: tutorMoveFailure(cause) }],
        });
      } else if (clearSavedMoveAttempt()) {
        setKey(tutorMoveRetryKey(attempt.key, cause, idempotencyKey));
        setUncertain(false);
        setError(tutorMoveFailure(cause));
        setPreview(null);
        setOverrideWorkingHours(false);
        autoPreviewTarget.current = null;
      } else {
        setUncertain(true);
        setError("The move was rejected, but its saved recovery record could not be cleared. Keep it locked and ask the office to review the lesson before starting another move.");
      }
    } finally {
      setMoveBusy(null);
    }
  }

  async function retrySavedMove() {
    if (pendingAttempt === null || busy !== null || recoveryProblem !== null) return;
    await submitMoveAttempt(pendingAttempt);
  }

  async function confirm(
    requestedStartsAt: string | null = targetStartsAt,
    currentVerdict: MoveVerdict | null = verdict,
    workingHoursOverride = overrideWorkingHours,
  ) {
    if (
      requestedStartsAt === null ||
      !currentVerdict?.canMove ||
      pendingAttempt !== null ||
      recoveryProblem !== null
    ) return;
    const attempt = {
      appointmentId,
      startsAt: requestedStartsAt,
      key,
      notify,
      overrideWorkingHours: workingHoursOverride,
    };
    const savedAttempt: PendingTutorMoveAttempt = {
      ...attempt,
      signature: tutorMoveAttemptSignature(attempt),
    };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(savedAttempt));
    } catch {
      setError("The move request could not be saved for recovery. No lesson was moved.");
      return;
    }
    setPendingAttempt(savedAttempt);
    await submitMoveAttempt(savedAttempt);
  }

  async function moveAnyway() {
    if (pendingAttempt !== null || recoveryProblem !== null) return;
    const requestedStartsAt = targetStartsAt;
    if (requestedStartsAt === null) return;
    const overrideVerdict = await look(true, requestedStartsAt);
    if (overrideVerdict?.canMove) {
      await confirm(requestedStartsAt, overrideVerdict, true);
    }
  }

  return (
    <div className="panel change-lesson" aria-labelledby="tutor-move-heading">
      <strong id="tutor-move-heading">Reschedule lesson</strong>
      <p className="small muted">
        Student: {studentName}
        <br />
        Lesson: {serviceName ?? "Lesson"}
        <br />
        Current time: {formatDate(startsAt)} at {formatTime(startsAt)}
      </p>

      {done ? (
        !hasHost && <ActionConfirmation {...done} reveal />
      ) : (
        <>
          {bookingId !== null &&
            initialStartsAt !== null &&
            selectedStart !== initialStartsAt && (
              <p className="small muted">
                Dragged target: {formatDate(initialStartsAt)} at{" "}
                {formatTime(initialStartsAt)}. Nothing has moved; preview will
                check whether that exact time is available or can be overridden.
              </p>
            )}
          {bookingId !== null &&
            initialStartsAt !== null &&
            selectedStart === initialStartsAt && (
              <div className="notice small">
                The dragged target is available. Nothing has moved yet; preview
                and confirmation are still required.
              </div>
            )}

          {bookingId !== null && (
            <section aria-labelledby="tutor-move-availability-heading">
              <div className="spread" style={{ gap: 8, margin: "10px 0 8px" }}>
                <strong id="tutor-move-availability-heading">
                  Available alternatives
                </strong>
                <span className="small muted">
                  {tutorMoveRangeLabel(searchDate)}
                </span>
              </div>

              <div
                className="row"
                style={{ gap: 8, marginBottom: 10, flexWrap: "wrap" }}
              >
                <button
                  className="quiet small"
                  type="button"
                  disabled={targetChangingDisabled}
                  onClick={() => shift(-1)}
                >
                  A week earlier
                </button>
                <button
                  className="quiet small"
                  type="button"
                  disabled={targetChangingDisabled}
                  onClick={() => shift(1)}
                >
                  A week later
                </button>
                <label className="row" style={{ gap: 6 }}>
                  <span className="small muted">Jump to date</span>
                  <input
                    aria-label="Jump to date"
                    type="date"
                    value={searchDate}
                    disabled={targetChangingDisabled}
                    onChange={(event) => chooseSearchDate(event.target.value)}
                  />
                </label>
              </div>

              {coarsePointer && (
                <div className="row">
                  <button
                    className="quiet small"
                    type="button"
                    disabled={targetChangingDisabled}
                    onClick={() => chooseQuickTarget(7)}
                  >
                    Same time next week
                  </button>
                  <button
                    className="quiet small"
                    type="button"
                    disabled={targetChangingDisabled}
                    onClick={() => chooseQuickTarget(1)}
                  >
                    +1 day
                  </button>
                </div>
              )}

              {busy === "context" && (
                <p className="small muted">Loading appointment details…</p>
              )}
              {busy === "availability" && (
                <p className="small muted">Loading available times…</p>
              )}
              {availabilityProblem && (
                <div className="error small">
                  {availabilityProblem}{" "}
                  <button
                    className="link"
                    type="button"
                    disabled={isBusy}
                    onClick={retryLoads}
                  >
                    Try again
                  </button>
                </div>
              )}

              {visibleSlots !== null &&
                visibleSlots.length === 0 &&
                !availabilityProblem && (
                  <p className="empty">
                    No normal available times in this week. Try another week, or
                    enter a time under Advanced.
                  </p>
                )}

              {visibleSlots !== null && visibleSlots.length > 0 && (
                <div className="availability-days">
                  {groupAvailabilitySlots(visibleSlots).map(
                    ([dateKeyValue, daySlots]) => (
                      <section className="availability-day" key={dateKeyValue}>
                        <h3 className="day-heading">
                          {formatDay(daySlots[0]!.starts_at)}
                        </h3>
                        <div className="slot-grid">
                          {daySlots.map((slot) => {
                            const selected = slot.starts_at === selectedStart;
                            return (
                              <button
                                key={slot.starts_at}
                                className={selected ? "" : "quiet"}
                                aria-pressed={selected}
                                disabled={targetChangingDisabled}
                                onClick={() => chooseSlot(slot.starts_at)}
                                type="button"
                              >
                                {formatTime(slot.starts_at)}
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    ),
                  )}
                </div>
              )}

              {selectedStart !== null && (
                <div className="notice" style={{ marginTop: 12 }}>
                  <strong>
                    Move to {formatDate(selectedStart)} at{" "}
                    {formatTime(selectedStart)}
                  </strong>
                </div>
              )}
            </section>
          )}

          <details
            open={advancedOpen}
            onToggle={(event) => handleAdvancedToggle(event.currentTarget.open)}
            style={{ marginTop: 12 }}
          >
            <summary>
              <strong>Advanced: enter a time manually</strong>
            </summary>
            {manual ? (
              <div style={{ marginTop: 10 }}>
                {bookingId === null && (
                  <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                    <button
                      className="quiet small"
                      type="button"
                      disabled={targetChangingDisabled}
                      onClick={() => shift(-1)}
                    >
                      A week earlier
                    </button>
                    <button
                      className="quiet small"
                      type="button"
                      disabled={targetChangingDisabled}
                      onClick={() => shift(1)}
                    >
                      A week later
                    </button>
                  </div>
                )}
                <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
                  <label className="stack">
                    <span className="small muted">New date</span>
                    <input
                      type="date"
                      value={date}
                      disabled={targetChangingDisabled}
                      onChange={(event) =>
                        retargetManual(() => setDate(event.target.value))
                      }
                    />
                  </label>
                  <label className="stack">
                    <span className="small muted">New time</span>
                    <input
                      type="time"
                      value={time}
                      disabled={targetChangingDisabled}
                      onChange={(event) =>
                        retargetManual(() => setTime(event.target.value))
                      }
                    />
                  </label>
                </div>
                <p className="small muted">
                  Manual times are not assumed free. Preview validates the exact
                  target and will only offer a schedule override for a safe
                  policy exception; real clashes remain blocked.
                </p>
                {bookingId !== null && (
                  <button
                    className="quiet small"
                    type="button"
                    disabled={targetChangingDisabled || context === null}
                    onClick={chooseAvailableTimes}
                  >
                    Show normal available times instead
                  </button>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 10 }}>
                <p className="small muted">
                  Use manual entry when the required time is not offered above.
                  Preview will still validate the exact target before anything
                  can move.
                </p>
                <button
                  className="quiet small"
                  type="button"
                  disabled={targetChangingDisabled}
                  onClick={chooseManual}
                >
                  Choose another time manually
                </button>
              </div>
            )}
          </details>

          {(pendingAttempt !== null || recoveryProblem !== null) && (
            <div
              className={recoveryProblem ? "error small" : "notice small"}
              role={recoveryProblem ? "alert" : "status"}
            >
              {pendingAttempt ? (
                <>
                  <p>A saved move request has not been confirmed.</p>
                  <p>
                    Target: {formatDate(pendingAttempt.startsAt)} at{" "}
                    {formatTime(pendingAttempt.startsAt)}. Check or retry this exact
                    request before making another change.
                  </p>
                  {error && <p>{error}</p>}
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => void retrySavedMove()}
                  >
                    {busy === "move" ? "Checking saved move…" : "Check or retry saved move"}
                  </button>
                </>
              ) : (
                <>
                  <p>{recoveryProblem}</p>
                  {error && <p>{error}</p>}
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={clearAndCheckLessonStatus}
                  >
                    Clear and check lesson status
                  </button>
                </>
              )}
            </div>
          )}

          {error && pendingAttempt === null && recoveryProblem === null && (
            <>{uncertain ? !hasHost && <ActionConfirmation variant="uncertain" title="Move outcome needs checking" description={error} reveal /> : <div className="error small" role="alert">{error}</div>}</>
          )}

          <div style={{ marginTop: 12 }}>
            <NotifyToggle
              checked={notify}
              onChange={setNotify}
              disabled={targetChangingDisabled}
              warn
              label="Email the student/family about the new time"
            />
          </div>

          {verdict ? (
            verdict.canOverrideWorkingHours && !overrideWorkingHours ? (
              <div className="notice" role="alert" style={{ marginTop: 12 }}>
                <p style={{ marginTop: 0 }}>
                  <strong>
                    {verdict.message.toLowerCase().includes("outside")
                      ? "Outside normal working hours."
                      : "Staff schedule override."}
                  </strong>{" "}
                  {verdict.message}
                </p>
                <p className="small">
                  Continuing overrides the applicable schedule policy for this
                  lesson only. The target will be checked again before anything
                  moves, and an existing diary block will remain in place.
                </p>
                <div className="row">
                  <button
                    type="button"
                    disabled={targetChangingDisabled}
                    onClick={() => void moveAnyway()}
                  >
                    Move anyway
                  </button>
                </div>
              </div>
            ) : (
              <>
                {busy === "preview" && (
                  <p className="small muted" role="status">
                    Checking…
                  </p>
                )}
                <p className="small" style={{ marginTop: 12 }}>
                  {verdict.canOverrideWorkingHours
                    ? "This time still needs a staff schedule override and could not be overridden."
                    : verdict.message}
                </p>
                <div className="row">
                  {verdict.canMove && targetStartsAt !== null && (
                    <button
                      type="button"
                      disabled={targetChangingDisabled}
                      onClick={() => void confirm()}
                    >
                      {busy === "move"
                        ? "Moving…"
                        : `Move to ${formatDate(targetStartsAt)} ${formatTime(
                            targetStartsAt,
                          )}`}
                    </button>
                  )}
                </div>
              </>
            )
          ) : (
            <>
              {!target.ok && (
                <p className="small muted" role="status" style={{ marginBottom: 0 }}>
                  {target.message}
                </p>
              )}
              {busy === "preview" && (
                <p className="small muted" role="status">
                  Checking…
                </p>
              )}
              {previewFailed && target.ok && (
                <button
                  style={{ marginTop: 12 }}
                  type="button"
                  disabled={targetChangingDisabled}
                  onClick={() => void look(false)}
                >
                  Check again
                </button>
              )}
            </>
          )}
          <div className="row">
            <button
              className="quiet"
              type="button"
              disabled={busy === "move"}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
